import {SecurityMapOf} from "../../security/common/map/SecurityMap";
import {TechnicalIndicatorsRecord, TechnicalIndicatorsRecords} from "../types/TechnicalIndicatorsRecord";
import {BehaviorSubject, Observable, ReplaySubject, Subject} from "rxjs";
import {CodeAndExchange, Securities} from "../../instruments/types/CodeAndExchange";
import {DateTimeUtil} from "../../js/util/DateTimeUtil";
import {TechnicalIndicatorsService} from "../service/TechnicalIndicatorsService";
import {ClientEventService} from "../../services/client-events/ClientEventService";
import {SystemEventService} from "../../services/system-state/SystemEventService";
import {SecurityUtil} from "../../js/util/SecurityUtil";
import {ClientEventType} from "../../services/client-events/models/ClientEventType";
import {SystemEvent} from "../../services/system-state/SystemEvent";
import {IsoDate} from "../../pseudo-types/DateTypes";
import {PartialObserver} from "rxjs/Observer";
import {ArrayUtil} from "../../js/util/ArrayUtil";

// TODO Once TechnicalIndicators is using CodeAndExchange this can extend SecurityDataStore
export class TechnicalIndicatorsStore {

    public static readonly id = "technicalIndicatorsStore";

    private readonly sources = SecurityMapOf<Subject<TechnicalIndicatorsRecord>>();

    private readonly requiresRequest$ = new ReplaySubject<CodeAndExchange>(1);
    private readonly lastReload$ = new BehaviorSubject(DateTimeUtil.isoToday());

    /** @ngInject */
    public constructor(private readonly technicalIndicatorsService: TechnicalIndicatorsService,
                       private readonly clientEventService: ClientEventService,
                       private readonly systemEventService: SystemEventService) {
        this.init();
    }

    private init() {
        this.bufferRequests();
        this.watchInfoUpdates();
        this.watchIdleEnd();
    }

    private bufferRequests() {
        const buffer$ = this.requiresRequest$.throttleTime(100).delay(100);
        this.requiresRequest$
            .buffer(buffer$)
            .filter((buffer) => buffer.length > 0)
            .switchMap((securities) => this.retrieveIndicators(securities))
            .subscribe((records) => this.updateIndicators(records));
    }

    private retrieveIndicators(securities: Securities): Observable<TechnicalIndicatorsRecords> {
        return Observable.of(securities)
                         .flatMap((s) => this.technicalIndicatorsService.latest(s))
                         .catch((err) => Observable.of([]));
    }

    private updateIndicators(records: TechnicalIndicatorsRecords) {
        records.forEach((record) => {
            try {
                // Since technical indicators doesn't currently use Exchange, we have to slightly hack this and hope
                const code = record.code;
                const exchange = SecurityUtil.guessExchange(code);
                const security = { code, exchange };
                const subject = this.sources.computeIfAbsent(security, () => new ReplaySubject<TechnicalIndicatorsRecord>(1));
                subject.next(record);
            } catch (e) {
            }
        });
    }

    private watchInfoUpdates() {
        this.clientEventService
            .events()
            .filter((evt) => evt.type === ClientEventType.SECURITY_INFO_UPDATED)
            .subscribe(() => this.reloadAll());
    }

    /* When we end idle we may have missed an update event due to overnight, so we reload manually if so */
    private watchIdleEnd() {
        this.systemEventService
            .idle()
            .filter((evt) => evt === SystemEvent.IDLE_END)
            .withLatestFrom(this.lastReload$)
            .filter(([_, lastReload]) => this.isNewDay(lastReload))
            .subscribe(() => this.reloadAll());
    }

    private isNewDay(lastReload: IsoDate): boolean {
        const now = DateTimeUtil.isoToday();
        return now > lastReload;
    }

    private reloadAll() {
        const observer: PartialObserver<TechnicalIndicatorsRecords> = {
            next: (records) => this.updateIndicators(records),
            complete: () => this.lastReload$.next(DateTimeUtil.isoToday())
        };

        Observable.of(this.sources)
                  .map((s) => s.keys())
                  .flatMap((keys) => this.retrieveIndicators(keys))
                  .subscribe(observer);
    }

    public stream(security: CodeAndExchange | Securities): Observable<TechnicalIndicatorsRecord> {
        if (ArrayUtil.isArray(security)) {
            const sources = security.map((s) => this.stream(s));
            return Observable.merge(...sources);
        } else {
            return this.source(security);
        }
    }

    private source(security: CodeAndExchange): Observable<TechnicalIndicatorsRecord> {
        if (this.sources.has(security)) {
            return this.sources.get(security)!;
        } else {
            const source = new ReplaySubject<TechnicalIndicatorsRecord>(1);
            this.sources.set(security, source);
            this.requiresRequest$.next(security);
            return source;
        }
    }
}
